import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { Button } from '../../../../shared/ui/Button/index.js'

import styles from './CatalogStatusRow.module.scss'

/**
 * Status row displayed above the sort row and product listing.
 *
 * Shows a count of filtered vs. total products and a button for resetting or
 * restoring filters. The entire row is suppressed when there is nothing to
 * communicate (no active filters and no restore snapshot available).
 *
 * Does NOT consume CatalogContext — all data passed as props by CatalogView.
 *
 * @param {object}        props
 * @param {number}        props.filteredCount       - currently visible products
 * @param {number}        props.totalCount          - total displayable products
 * @param {number}        props.activeFiltersCount  - number of filters deviating from default
 * @param {boolean}       props.canRestorePreReset  - true immediately after a reset
 * @param {() => void}    props.onReset
 * @param {() => void}    props.onRestorePreReset
 */
export function CatalogStatusRow({
  filteredCount,
  totalCount,
  activeFiltersCount,
  canRestorePreReset,
  onReset,
  onRestorePreReset,
}) {
  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()

  const showCount = activeFiltersCount !== 0

  return (
    <div className={styles.wrapper}>
      <div className={styles.row}>
        {showCount && (
          <p className={styles.count}>
            {t('catalog.results_found', {
              filtered: filteredCount,
              total:    totalCount,
            })}
          </p>
        )}

        {/* Spacer pushes button to the right when count is hidden */}
        {!showCount && <span />}

        <Button
          type="button"
          kind="text"
          size="large"
          onClick={canRestorePreReset ? onRestorePreReset : onReset}
          disabled={!canRestorePreReset && activeFiltersCount === 0}
        >
          {canRestorePreReset ? t('catalog.restore_all_filters') : t('catalog.reset_all_filters')}
        </Button>
      </div>
    </div>
  )
}
